"use client";

import { useState } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

type CandidateStatus =
    | "Invited"
    | "Preparing"
    | "References in Progress"
    | "Ready for Committee"
    | "Under Committee Review"
    | "Committee Approved"
    | "Board Approved — Processing"
    | "Certificate in Production"
    | "Certificate Shipped"
    | "Chartered"
    | "Parked — Building Experience"
    | "Flagged for Board";

interface Reference {
    name: string;
    email: string;
    status: "complete" | "pending" | "lapsed";
    scores?: { d1: number; d2: number; d3: number; d4: number };
    comments?: { d1: string; d2: string; d3: string; d4: string };
    recommendation?: "Recommend" | "Recommend with reservations" | "Do not recommend";
    aiOriginality?: "Yes" | "Unsure" | "No";
}

interface Candidate {
    id: string;
    name: string;
    employer: string;
    location: string;
    path: "A" | "B";
    status: CandidateStatus;
    daysInState: number;
    dateApplied: string;
    l3PassDate?: string;
    yearsExperience: number;
    workExperience: {
        context: string;
        accountability: string;
        outputs: string;
        cadence: string;
    };
    references: Reference[];
    cascadeStep?: number;
    urgency: "green" | "yellow" | "red";
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const initialCandidates: Candidate[] = [
    {
        id: "1",
        name: "Tanmay Sharma",
        employer: "Fidelity Investments",
        location: "Boston, MA",
        path: "B",
        status: "References in Progress",
        daysInState: 18,
        dateApplied: "2026-03-30",
        l3PassDate: "2025-12-15",
        yearsExperience: 4,
        workExperience: {
            context: "Senior Portfolio Analyst at Fidelity Investments, Equity Research division.",
            accountability: "Report to Director of Research. Produce daily sector rotation models for portfolio managers.",
            outputs: "Weekly technical analysis reports, sector momentum scorecards, monthly client presentations.",
            cadence: "4 years of continuous full-time professional activity.",
        },
        references: [
            { name: "Michael Chang", email: "mchang@cmt.org", status: "complete", scores: { d1: 5, d2: 4, d3: 5, d4: 5 }, comments: { d1: "Tanmay is exceptionally proactive.", d2: "Work is clearly his own.", d3: "Very humble, always learning.", d4: "Would be a great addition." }, recommendation: "Recommend", aiOriginality: "Yes" },
            { name: "Priya Nair", email: "pnair@hedgefund.com", status: "lapsed", scores: undefined, comments: undefined, recommendation: undefined },
            { name: "David Kim", email: "dkim@cmt.org", status: "pending" },
        ],
        urgency: "red",
    },
    {
        id: "2",
        name: "Logan Mitchell",
        employer: "Raymond James",
        location: "St. Petersburg, FL",
        path: "B",
        status: "Preparing",
        daysInState: 2,
        dateApplied: "2026-04-15",
        l3PassDate: "2026-01-10",
        yearsExperience: 3,
        workExperience: {
            context: "Financial Advisor at Raymond James.",
            accountability: "Report to Branch Manager. Serve retail clients with portfolio construction.",
            outputs: "Client investment proposals, quarterly performance reviews.",
            cadence: "3 years full-time.",
        },
        references: [
            { name: "", email: "", status: "pending" },
            { name: "", email: "", status: "pending" },
            { name: "", email: "", status: "pending" },
        ],
        urgency: "green",
    },
    {
        id: "3",
        name: "Nicola Ferretti",
        employer: "Mediobanca",
        location: "Milan, Italy",
        path: "B",
        status: "Board Approved — Processing",
        daysInState: 5,
        dateApplied: "2026-02-01",
        l3PassDate: "2025-06-20",
        yearsExperience: 6,
        workExperience: {
            context: "Head of Technical Research at Mediobanca.",
            accountability: "Report to Chief Investment Officer. Lead a team of 4 analysts.",
            outputs: "Proprietary charting system, weekly macro-technical briefs for institutional clients.",
            cadence: "6 years, primary professional activity.",
        },
        references: [
            { name: "Carlo Bianchi", email: "cbianchi@cmt.org", status: "complete", scores: { d1: 5, d2: 5, d3: 5, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Outstanding.", d2: "Clearly his own work.", d3: "Extremely humble.", d4: "Perfect fit." } },
            { name: "Sofia Ricci", email: "sricci@cmt.org", status: "complete", scores: { d1: 5, d2: 4, d3: 5, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Very engaged.", d2: "High quality.", d3: "Great character.", d4: "Yes." } },
            { name: "Marco Vitale", email: "mvitale@cmt.org", status: "complete", scores: { d1: 4, d2: 5, d3: 4, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Initiative shown.", d2: "Rigorous.", d3: "Selfless.", d4: "Recommend." } },
        ],
        cascadeStep: 3,
        urgency: "green",
    },
    {
        id: "4",
        name: "Andrew Park",
        employer: "Goldman Sachs",
        location: "New York, NY",
        path: "B",
        status: "Committee Approved",
        daysInState: 4,
        dateApplied: "2026-02-20",
        l3PassDate: "2025-09-01",
        yearsExperience: 5,
        workExperience: {
            context: "Vice President, Equity Derivatives at Goldman Sachs.",
            accountability: "Report to Managing Director. Manage technical overlay for derivatives desk.",
            outputs: "Daily options flow analysis, volatility surface models, client briefings.",
            cadence: "5 years primary activity.",
        },
        references: [
            { name: "Janet Wu", email: "jwu@cmt.org", status: "complete", scores: { d1: 5, d2: 5, d3: 4, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Exceptional.", d2: "Clearly his work.", d3: "Collaborative.", d4: "Strong yes." } },
            { name: "Robert Chen", email: "rchen@cmt.org", status: "complete", scores: { d1: 4, d2: 4, d3: 5, d4: 4 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Good engagement.", d2: "Original.", d3: "Humble.", d4: "Yes." } },
            { name: "Lisa Tran", email: "ltran@cmt.org", status: "complete", scores: { d1: 5, d2: 5, d3: 5, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Top performer.", d2: "Rigorous.", d3: "Selfless.", d4: "Absolutely." } },
        ],
        urgency: "yellow",
    },
    {
        id: "5",
        name: "Nadezda Kovacs",
        employer: "N/A",
        location: "Prague, Czech Republic",
        path: "B",
        status: "Parked — Building Experience",
        daysInState: 45,
        dateApplied: "2026-03-01",
        l3PassDate: "2025-12-10",
        yearsExperience: 1,
        workExperience: {
            context: "Recent Level 3 passer, currently in graduate school.",
            accountability: "Teaching assistant for finance courses.",
            outputs: "Academic papers, course materials.",
            cadence: "1 year, part-time academic role.",
        },
        references: [],
        urgency: "yellow",
    },
    {
        id: "6",
        name: "Shubham Desai",
        employer: "ICICI Bank",
        location: "Mumbai, India",
        path: "B",
        status: "Under Committee Review",
        daysInState: 9,
        dateApplied: "2026-02-10",
        l3PassDate: "2025-06-05",
        yearsExperience: 4,
        workExperience: {
            context: "Senior Technical Analyst at ICICI Bank, Wealth Management.",
            accountability: "Report to Head of Research. Provide technical overlay for wealth advisors.",
            outputs: "Daily market briefs, sector rotation models, client education webinars.",
            cadence: "4 years full-time.",
        },
        references: [
            { name: "Rahul Mehta", email: "rmehta@cmt.org", status: "complete", scores: { d1: 4, d2: 3, d3: 4, d4: 4 }, recommendation: "Recommend with reservations", aiOriginality: "Unsure", comments: { d1: "Generally engaged.", d2: "Some sections seem AI-assisted.", d3: "Good character.", d4: "Probably a good fit." } },
            { name: "Ananya Patel", email: "apatel@cmt.org", status: "complete", scores: { d1: 5, d2: 4, d3: 5, d4: 4 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Very proactive.", d2: "His own work.", d3: "Humble.", d4: "Yes." } },
            { name: "Vikram Singh", email: "vsingh@cmt.org", status: "complete", scores: { d1: 3, d2: 2, d3: 3, d4: 3 }, recommendation: "Recommend with reservations", aiOriginality: "Unsure", comments: { d1: "Average initiative.", d2: "Concerned about originality.", d3: "Neutral.", d4: "With reservations." } },
        ],
        urgency: "yellow",
    },
    {
        id: "7",
        name: "Ali Begovic",
        employer: "Erste Group",
        location: "Vienna, Austria",
        path: "A",
        status: "Chartered",
        daysInState: 30,
        dateApplied: "2025-11-01",
        yearsExperience: 8,
        workExperience: {
            context: "Chief Market Strategist at Erste Group.",
            accountability: "Report to CIO. Lead technical research for entire bank.",
            outputs: "Monthly outlook reports, proprietary indicator suite, media appearances.",
            cadence: "8 years primary activity.",
        },
        references: [
            { name: "Hans Mueller", email: "hmueller@cmt.org", status: "complete", scores: { d1: 5, d2: 5, d3: 5, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Exceptional.", d2: "His own.", d3: "Exemplary.", d4: "Absolute yes." } },
            { name: "Elena Weber", email: "eweber@cmt.org", status: "complete", scores: { d1: 5, d2: 5, d3: 5, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Leader.", d2: "Original.", d3: "Selfless.", d4: "Yes." } },
            { name: "Peter Novak", email: "pnovak@cmt.org", status: "complete", scores: { d1: 5, d2: 4, d3: 5, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Initiative.", d2: "Rigorous.", d3: "Humble.", d4: "Yes." } },
        ],
        urgency: "green",
    },
    {
        id: "8",
        name: "Michael Torres",
        employer: "Merrill Lynch",
        location: "Dallas, TX",
        path: "B",
        status: "Preparing",
        daysInState: 7,
        dateApplied: "2026-04-10",
        l3PassDate: "2026-01-20",
        yearsExperience: 3,
        workExperience: {
            context: "Financial Advisor at Merrill Lynch.",
            accountability: "Report to Senior FA. Serve UHNW clients.",
            outputs: "Client portfolio proposals, technical overlay for equity selection.",
            cadence: "3 years full-time.",
        },
        references: [],
        urgency: "green",
    },
    {
        id: "9",
        name: "Sarah Chen",
        employer: "East Capital",
        location: "Stockholm, Sweden",
        path: "B",
        status: "Ready for Committee",
        daysInState: 3,
        dateApplied: "2026-03-15",
        l3PassDate: "2025-09-30",
        yearsExperience: 5,
        workExperience: {
            context: "Senior Quantitative Analyst at East Capital, Research group.",
            accountability: "Report to Head of Risk. Produce weekly macro-technical models for portfolio managers.",
            outputs: "Weekly factor rotation models, charting decks for internal investment committee.",
            cadence: "5 years of continuous, full-time professional activity.",
        },
        references: [
            { name: "Lars Eriksson", email: "leriksson@cmt.org", status: "complete", scores: { d1: 5, d2: 5, d3: 5, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Exceptional initiative.", d2: "Clearly her own work.", d3: "Extremely humble.", d4: "Strong yes." } },
            { name: "Ingrid Holm", email: "iholm@cmt.org", status: "complete", scores: { d1: 4, d2: 5, d3: 4, d4: 5 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Very proactive.", d2: "Original and rigorous.", d3: "Open to learning.", d4: "Yes." } },
            { name: "Bjorn Lindgren", email: "blindgren@cmt.org", status: "complete", scores: { d1: 5, d2: 4, d3: 5, d4: 4 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "High initiative.", d2: "Her work.", d3: "Selfless contributor.", d4: "Recommend." } },
        ],
        urgency: "green",
    },
    {
        id: "10",
        name: "Nathan Brubaker",
        employer: "Self-employed",
        location: "Austin, TX",
        path: "A",
        status: "Flagged for Board",
        daysInState: 12,
        dateApplied: "2026-01-20",
        yearsExperience: 4,
        workExperience: {
            context: "Independent technical analysis consultant and educator.",
            accountability: "Self-directed. Serves retail traders and small RIAs.",
            outputs: "Educational courses, trading newsletters, YouTube channel with 80K subscribers.",
            cadence: "4 years, full-time independent practice.",
        },
        references: [
            { name: "Greg Harmon", email: "gharmon@cmt.org", status: "complete", scores: { d1: 4, d2: 4, d3: 5, d4: 3 }, recommendation: "Recommend with reservations", aiOriginality: "Yes", comments: { d1: "Engaged educator.", d2: "Original content.", d3: "Community-minded.", d4: "Non-standard path — board should weigh in." } },
            { name: "Oliver Chen", email: "ochen@cmt.org", status: "complete", scores: { d1: 4, d2: 3, d3: 4, d4: 3 }, recommendation: "Recommend with reservations", aiOriginality: "Yes", comments: { d1: "Good initiative.", d2: "Retail-focused.", d3: "Humble.", d4: "Borderline on experience standard." } },
            { name: "Lee Adams", email: "ladams@cmt.org", status: "complete", scores: { d1: 5, d2: 4, d3: 5, d4: 4 }, recommendation: "Recommend", aiOriginality: "Yes", comments: { d1: "Excellent educator.", d2: "His own work.", d3: "Very selfless.", d4: "Yes — board override warranted." } },
        ],
        urgency: "yellow",
    },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────


const LIKERT_LABELS = ["", "Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"];
const LIKERT_COLORS = ["", "#ef4444", "#f97316", "#6b7280", "#22c55e", "#16a34a"];

function ScoreDot({ score }: { score: number }) {
    return (
        <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold"
            style={{ backgroundColor: LIKERT_COLORS[score] ?? "#6b7280" }}
            title={LIKERT_LABELS[score]}
        >
            {score}
        </span>
    );
}

function StatusBadge({ status }: { status: CandidateStatus }) {
    const colors: Record<string, string> = {
        "Chartered": "bg-emerald-100 text-emerald-800",
        "Board Approved — Processing": "bg-blue-100 text-blue-800",
        "Committee Approved": "bg-cyan-100 text-cyan-800",
        "Ready for Committee": "bg-violet-100 text-violet-800",
        "Under Committee Review": "bg-indigo-100 text-indigo-800",
        "References in Progress": "bg-amber-100 text-amber-800",
        "Preparing": "bg-orange-100 text-orange-800",
        "Invited": "bg-gray-100 text-gray-800",
        "Parked — Building Experience": "bg-stone-100 text-stone-700",
        "Flagged for Board": "bg-rose-100 text-rose-800",
        "Certificate in Production": "bg-teal-100 text-teal-800",
        "Certificate Shipped": "bg-sky-100 text-sky-800",
    };
    return (
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colors[status] ?? "bg-gray-100 text-gray-700"}`}>
            {status}
        </span>
    );
}

function UrgencyDot({ urgency }: { urgency: "green" | "yellow" | "red" }) {
    const cls = { green: "bg-green-400", yellow: "bg-yellow-400", red: "bg-red-500" };
    return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls[urgency]}`} />;
}

function RefStatusBadge({ status }: { status: Reference["status"] }) {
    if (status === "complete") return <span className="text-green-600 font-semibold text-xs">✓ Complete</span>;
    if (status === "lapsed") return <span className="text-yellow-600 font-semibold text-xs">⚠ Lapsed dues</span>;
    return <span className="text-gray-400 font-semibold text-xs">○ Pending</span>;
}

// ─── View 1: Candidate Form ────────────────────────────────────────────────────

function CandidateForm({ onSubmit }: { onSubmit: (candidate: Candidate) => void }) {
    const [step, setStep] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phone: "",
        address: "",
        employer: "",
        context: "",
        accountability: "",
        outputs: "",
        cadenceYears: "3",
        cadenceMonths: "0",
        isRegularActivity: true,
        path: "B" as "A" | "B",
        ref1Name: "", ref1Email: "", ref1Status: "" as "" | "valid" | "lapsed" | "notfound",
        ref2Name: "", ref2Email: "", ref2Status: "" as "" | "valid" | "lapsed" | "notfound",
        ref3Name: "", ref3Email: "", ref3Status: "" as "" | "valid" | "lapsed" | "notfound",
        pcsAgreed: false,
    });

    const simulateRefLookup = (email: string): "valid" | "lapsed" | "notfound" => {
        if (email.includes("@cmt.org")) return "valid";
        if (email.includes("lapsed")) return "lapsed";
        if (email.length > 5) return "notfound";
        return "notfound";
    };

    const handleRefEmailChange = (idx: 1 | 2 | 3, val: string) => {
        const statusKey = `ref${idx}Status` as "ref1Status" | "ref2Status" | "ref3Status";
        const emailKey = `ref${idx}Email` as "ref1Email" | "ref2Email" | "ref3Email";
        const status = val.length > 6 ? simulateRefLookup(val) : "";
        setForm(f => ({ ...f, [emailKey]: val, [statusKey]: status }));
    };

    const handleSubmit = () => {
        const newCandidate: Candidate = {
            id: Date.now().toString(),
            name: form.fullName || "New Applicant",
            employer: form.employer || "Unknown",
            location: form.address || "Unknown",
            path: form.path,
            status: "References in Progress",
            daysInState: 0,
            dateApplied: new Date().toISOString().split("T")[0],
            l3PassDate: "2025-12-01",
            yearsExperience: parseInt(form.cadenceYears),
            workExperience: {
                context: form.context,
                accountability: form.accountability,
                outputs: form.outputs,
                cadence: `${form.cadenceYears} years ${form.cadenceMonths} months, ${form.isRegularActivity ? "regular professional activity" : "part-time"}.`,
            },
            references: [
                { name: form.ref1Name, email: form.ref1Email, status: "pending" },
                { name: form.ref2Name, email: form.ref2Email, status: "pending" },
                { name: form.ref3Name, email: form.ref3Email, status: "pending" },
            ],
            urgency: "green",
        };
        onSubmit(newCandidate);
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="max-w-xl mx-auto mt-16 text-center p-8 border border-green-200 bg-green-50 rounded-lg">
                <div className="text-4xl mb-4">✓</div>
                <h2 className="text-xl font-bold text-green-800 mb-2">Application Submitted</h2>
                <p className="text-green-700">Your application has been received. Your three references will now be contacted by email.</p>
                <p className="text-sm text-green-600 mt-4">You can track your application status through Barbara's Dashboard.</p>
            </div>
        );
    }

    const steps = ["Personal Info", "Work Experience", "Membership Path", "References", "Documents & PCS"];

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8">
                <div className="flex gap-2 mb-2">
                    {steps.map((s, i) => (
                        <button
                            key={s}
                            onClick={() => setStep(i)}
                            className={`flex-1 py-1.5 text-xs font-semibold rounded transition-all ${i === step ? "bg-[#003d45] text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}
                        >
                            {i < step ? "✓ " : ""}{s}
                        </button>
                    ))}
                </div>
                <div className="h-1 bg-gray-100 rounded">
                    <div className="h-1 bg-[#003d45] rounded transition-all" style={{ width: `${((step) / (steps.length - 1)) * 100}%` }} />
                </div>
            </div>

            {step === 0 && (
                <div className="space-y-4">
                    <h3 className="font-bold text-gray-800">Personal Information</h3>
                    {[
                        { label: "Full Legal Name", key: "fullName", placeholder: "As it will appear on your certificate" },
                        { label: "Email Address", key: "email", placeholder: "your@email.com" },
                        { label: "Phone Number", key: "phone", placeholder: "+1 (555) 000-0000" },
                        { label: "Physical Address", key: "address", placeholder: "City, State, Country" },
                        { label: "Current Employer", key: "employer", placeholder: "Company or practice name" },
                    ].map(({ label, key, placeholder }) => (
                        <div key={key}>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                            <input
                                className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#003d45]"
                                placeholder={placeholder}
                                value={(form as any)[key]}
                                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                            />
                        </div>
                    ))}
                    <button onClick={() => setStep(1)} className="mt-4 px-6 py-2 bg-[#003d45] text-white rounded text-sm font-semibold">
                        Continue →
                    </button>
                </div>
            )}

            {step === 1 && (
                <div className="space-y-4">
                    <h3 className="font-bold text-gray-800">Work Experience</h3>
                    <p className="text-xs text-gray-500">Answer each question — the system maps your answers to our evaluation standards.</p>
                    {[
                        { label: "Where do you currently work, or what is your professional practice?", key: "context", tag: "Context" },
                        { label: "What is your role, and who do you report to or serve?", key: "accountability", tag: "Accountability" },
                        { label: "Describe the professional work products you create (reports, analyses, models, etc.)", key: "outputs", tag: "Outputs" },
                    ].map(({ label, key, tag }) => (
                        <div key={key}>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {label} <span className="ml-1 px-1.5 py-0.5 bg-[#003d45]/10 text-[#003d45] rounded text-[10px] font-bold">{tag}</span>
                            </label>
                            <textarea
                                rows={2}
                                className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#003d45]"
                                value={(form as any)[key]}
                                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                            />
                        </div>
                    ))}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">How long have you been doing this work? <span className="ml-1 px-1.5 py-0.5 bg-[#003d45]/10 text-[#003d45] rounded text-[10px] font-bold">Cadence</span></label>
                        <div className="flex gap-3 items-center">
                            <select className="border border-gray-200 rounded px-2 py-2 text-sm" value={form.cadenceYears} onChange={e => setForm(f => ({ ...f, cadenceYears: e.target.value }))}>
                                {Array.from({ length: 20 }, (_, i) => i + 1).map(y => <option key={y}>{y}</option>)}
                            </select>
                            <span className="text-sm text-gray-500">years</span>
                            <select className="border border-gray-200 rounded px-2 py-2 text-sm" value={form.cadenceMonths} onChange={e => setForm(f => ({ ...f, cadenceMonths: e.target.value }))}>
                                {Array.from({ length: 12 }, (_, i) => i).map(m => <option key={m}>{m}</option>)}
                            </select>
                            <span className="text-sm text-gray-500">months</span>
                        </div>
                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                            <input type="checkbox" checked={form.isRegularActivity} onChange={e => setForm(f => ({ ...f, isRegularActivity: e.target.checked }))} />
                            <span className="text-sm text-gray-600">This is my regular professional activity</span>
                        </label>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setStep(0)} className="px-4 py-2 border border-gray-200 rounded text-sm">← Back</button>
                        <button onClick={() => setStep(2)} className="px-6 py-2 bg-[#003d45] text-white rounded text-sm font-semibold">Continue →</button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="space-y-4">
                    <h3 className="font-bold text-gray-800">Membership Path</h3>
                    <div className="p-4 border border-[#003d45]/20 bg-[#003d45]/5 rounded">
                        <p className="text-sm font-semibold text-[#003d45]">
                            Based on your information, you qualify under <strong>Path {form.path === "B" ? "B (3+ years + all CMT levels)" : "A (5+ years)"}</strong>.
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Is this correct?</p>
                    </div>
                    <div className="space-y-2">
                        <label className="flex items-start gap-3 cursor-pointer p-3 border rounded hover:bg-gray-50">
                            <input type="radio" name="path" checked={form.path === "B"} onChange={() => setForm(f => ({ ...f, path: "B" }))} className="mt-0.5" />
                            <div>
                                <div className="text-sm font-semibold">Path B — 3 years + all CMT levels</div>
                                <div className="text-xs text-gray-500">I have passed all three CMT levels and have ≥3 years of approved professional work experience.</div>
                            </div>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer p-3 border rounded hover:bg-gray-50">
                            <input type="radio" name="path" checked={form.path === "A"} onChange={() => setForm(f => ({ ...f, path: "A" }))} className="mt-0.5" />
                            <div>
                                <div className="text-sm font-semibold">Path A — 5 years experience</div>
                                <div className="text-xs text-gray-500">I have ≥5 years of approved professional work experience. CMT exam levels are not required.</div>
                            </div>
                        </label>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setStep(1)} className="px-4 py-2 border border-gray-200 rounded text-sm">← Back</button>
                        <button onClick={() => setStep(3)} className="px-6 py-2 bg-[#003d45] text-white rounded text-sm font-semibold">Continue →</button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="space-y-4">
                    <h3 className="font-bold text-gray-800">References</h3>
                    <p className="text-xs text-gray-500">You need three CMT Professional Members in good standing. Enter their membership email to verify status in real time.</p>
                    {([1, 2, 3] as const).map(i => {
                        const nameKey = `ref${i}Name` as "ref1Name" | "ref2Name" | "ref3Name";
                        const emailKey = `ref${i}Email` as "ref1Email" | "ref2Email" | "ref3Email";
                        const statusKey = `ref${i}Status` as "ref1Status" | "ref2Status" | "ref3Status";
                        const refStatus = form[statusKey];
                        return (
                            <div key={i} className="p-4 border border-gray-200 rounded">
                                <div className="text-xs font-bold text-gray-500 mb-2">REFERENCE {i}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
                                        <input className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#003d45]" placeholder="Full name" value={form[nameKey]} onChange={e => setForm(f => ({ ...f, [nameKey]: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Membership Email</label>
                                        <div className="relative">
                                            <input
                                                className={`w-full border rounded px-3 py-2 text-sm focus:outline-none pr-8 ${refStatus === "valid" ? "border-green-400" : refStatus === "lapsed" ? "border-yellow-400" : refStatus === "notfound" ? "border-red-400" : "border-gray-200 focus:border-[#003d45]"}`}
                                                placeholder="email@cmt.org"
                                                value={form[emailKey]}
                                                onChange={e => handleRefEmailChange(i, e.target.value)}
                                            />
                                            {refStatus === "valid" && <span className="absolute right-2 top-2 text-green-500">✓</span>}
                                            {refStatus === "lapsed" && <span className="absolute right-2 top-2 text-yellow-500">⚠</span>}
                                            {refStatus === "notfound" && <span className="absolute right-2 top-2 text-red-500">✗</span>}
                                        </div>
                                        {refStatus === "valid" && <p className="text-xs text-green-600 mt-0.5">Professional Member — active</p>}
                                        {refStatus === "lapsed" && <p className="text-xs text-yellow-600 mt-0.5">This member's dues are not current</p>}
                                        {refStatus === "notfound" && <p className="text-xs text-red-600 mt-0.5">Not found in membership directory</p>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div className="flex gap-3">
                        <button onClick={() => setStep(2)} className="px-4 py-2 border border-gray-200 rounded text-sm">← Back</button>
                        <button onClick={() => setStep(4)} className="px-6 py-2 bg-[#003d45] text-white rounded text-sm font-semibold">Continue →</button>
                    </div>
                </div>
            )}

            {step === 4 && (
                <div className="space-y-4">
                    <h3 className="font-bold text-gray-800">Documents & Professional Conduct Statement</h3>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-2">Original Work Sample <span className="text-red-500">*</span></label>
                        <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center hover:border-[#003d45] cursor-pointer transition-colors">
                            <div className="text-2xl mb-2">📄</div>
                            <p className="text-sm text-gray-500">Click to upload or drag & drop</p>
                            <p className="text-xs text-gray-400 mt-1">PDF, DOCX, XLSX — your original analytical work</p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-2">Supporting Documents <span className="text-gray-400">(optional)</span></label>
                        <div className="border-2 border-dashed border-gray-200 rounded p-4 text-center hover:border-gray-400 cursor-pointer transition-colors">
                            <p className="text-sm text-gray-400">Employment letters, contracts, publications</p>
                        </div>
                    </div>
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded">
                        <p className="text-xs font-bold text-gray-700 mb-2">PROFESSIONAL CONDUCT STATEMENT</p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                            As a member of the CMT Association, I commit to upholding the highest standards of professional conduct, including honesty, integrity, and respect for the discipline of technical analysis. I attest that all materials I have submitted are my own work, and I have not used AI-generated content as a substitute for my own analytical thinking. I understand that violations of this conduct standard may result in revocation of membership.
                        </p>
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                            <input type="checkbox" checked={form.pcsAgreed} onChange={e => setForm(f => ({ ...f, pcsAgreed: e.target.checked }))} />
                            <span className="text-sm font-semibold text-gray-700">I have read and agree to the Professional Conduct Statement</span>
                        </label>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        💳 Your professional membership dues (<strong>$325</strong>) will be charged upon board approval.
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setStep(3)} className="px-4 py-2 border border-gray-200 rounded text-sm">← Back</button>
                        <button
                            onClick={handleSubmit}
                            disabled={!form.pcsAgreed}
                            className="px-6 py-2 bg-[#AE232D] text-white rounded text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c1121f] transition-colors"
                        >
                            Submit Application
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── View 2: Reference Evaluation ─────────────────────────────────────────────

// Pending evaluations — candidates whose references haven't all submitted yet
const PENDING_REF_CANDIDATES = [
    { id: "r1", name: "Sarah Chen", employer: "East Capital", path: "B" as const, l3PassDate: "2025-09-30", yearsExp: 5, refName: "Lars Eriksson" },
    { id: "r2", name: "Tanmay Sharma", employer: "Fidelity Investments", path: "B" as const, l3PassDate: "2025-12-15", yearsExp: 4, refName: "David Kim" },
    { id: "r3", name: "Shubham Desai", employer: "ICICI Bank", path: "B" as const, l3PassDate: "2025-06-05", yearsExp: 4, refName: "Vikram Singh" },
];

function RefEvalForm({ candidate }: { candidate: typeof PENDING_REF_CANDIDATES[0]; }) {
    const [submitted, setSubmitted] = useState(false);
    const [showOrientation, setShowOrientation] = useState(false);
    const [form, setForm] = useState({
        d1Rating: 0, d1Comment: "",
        d2Rating: 0, d2Comment: "", d2Originality: "" as "" | "Yes" | "Unsure" | "No",
        d3Rating: 0, d3Comment: "",
        d4Rating: 0, d4Comment: "",
        recommendation: "" as "" | "Recommend" | "Recommend with reservations" | "Do not recommend",
        hadConversation: "" as "" | "Yes" | "No",
    });

    const dimensions = [
        { key: "d1", title: "Professional Engagement", valueKey: "Initiative", statement: "This person actively practices or applies technical analysis in a professional context and demonstrates initiative in developing their skills and contributing to the discipline." },
        { key: "d2", title: "Quality of Work", valueKey: "Trustworthiness", statement: "The candidate's work sample reflects genuine analytical thinking, is clearly their own work, and demonstrates a level of rigor I would associate with a CMT Charterholder." },
        { key: "d3", title: "Professional Character", valueKey: "Humility & Selflessness", statement: "In my interaction with or knowledge of this candidate, they demonstrate openness to learning, respect for the discipline and other practitioners, and willingness to contribute to the community." },
        { key: "d4", title: "Community Fit", valueKey: "Synthesis", statement: "I believe this person will be a good addition to the CMT community and will uphold the values and reputation of the Association." },
    ];

    if (submitted) {
        return (
            <div className="mt-8 text-center p-8 border border-green-200 bg-green-50 rounded-lg">
                <div className="text-4xl mb-4">✓</div>
                <h2 className="text-xl font-bold text-green-800 mb-2">Evaluation Submitted</h2>
                <p className="text-green-700">Thank you for supporting <strong>{candidate.name}</strong>&apos;s application for Professional Membership. Your evaluation has been recorded.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="p-4 bg-[#003d45]/5 border border-[#003d45]/20 rounded">
                <p className="text-sm text-[#003d45] font-semibold">
                    Thank you for serving as a reference for <strong>{candidate.name}</strong>. You are helping welcome a new member into the CMT community.
                </p>
            </div>

            <div>
                <button
                    onClick={() => setShowOrientation(!showOrientation)}
                    className="flex items-center gap-2 text-sm font-semibold text-[#003d45] hover:underline"
                >
                    {showOrientation ? "▼" : "►"} Your role as a reference — read before proceeding
                </button>
                {showOrientation && (
                    <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600 space-y-2">
                        <p><strong>You are NOT a second examiner.</strong> The CMT Program has already validated {candidate.name.split(" ")[0]}&apos;s technical knowledge.</p>
                        <p><strong>You ARE evaluating</strong> professional character, genuine engagement with the discipline, and community fit.</p>
                        <p><strong>Recommended:</strong> Have a 30-minute conversation with the candidate — ask about their career, interest in technical analysis, and what being a Charterholder means to them.</p>
                        <p><strong>On AI-generated work:</strong> If the work sample appears substantially AI-generated, flag it. Look for: generic phrasing, lack of personal voice, analysis disconnected from stated professional context.</p>
                    </div>
                )}
            </div>

            {dimensions.map(({ key, title, valueKey, statement }) => (
                <div key={key} className="p-4 border border-gray-200 rounded space-y-3">
                    <div>
                        <div className="text-sm font-bold text-gray-800">{title}</div>
                        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Maps to: {valueKey}</div>
                    </div>
                    <p className="text-sm text-gray-600 italic">&ldquo;{statement}&rdquo;</p>
                    <div className="flex gap-2 flex-wrap">
                        {[1, 2, 3, 4, 5].map(score => (
                            <button
                                key={score}
                                onClick={() => setForm(f => ({ ...f, [`${key}Rating`]: score }))}
                                className={`px-3 py-1.5 rounded border text-xs font-semibold transition-all ${(form as any)[`${key}Rating`] === score ? "text-white border-transparent" : "border-gray-300 text-gray-600 hover:border-gray-400"}`}
                                style={(form as any)[`${key}Rating`] === score ? { backgroundColor: LIKERT_COLORS[score] } : {}}
                            >
                                {LIKERT_LABELS[score]}
                            </button>
                        ))}
                    </div>
                    <textarea
                        rows={2}
                        placeholder="Please explain briefly..."
                        className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#003d45]"
                        value={(form as any)[`${key}Comment`]}
                        onChange={e => setForm(f => ({ ...f, [`${key}Comment`]: e.target.value }))}
                    />
                    {key === "d2" && (
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Do you believe this work sample is the candidate&apos;s own work?</label>
                            <div className="flex gap-3">
                                {["Yes", "Unsure", "No"].map(opt => (
                                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-sm">
                                        <input type="radio" name={`d2originality-${candidate.id}`} checked={form.d2Originality === opt} onChange={() => setForm(f => ({ ...f, d2Originality: opt as any }))} />
                                        {opt}
                                    </label>
                                ))}
                            </div>
                            {(form.d2Originality === "Unsure" || form.d2Originality === "No") && (
                                <textarea rows={2} placeholder="Please explain..." className="mt-2 w-full border border-yellow-300 rounded px-3 py-2 text-sm focus:outline-none" />
                            )}
                        </div>
                    )}
                </div>
            ))}

            <div className="p-4 border border-gray-200 rounded space-y-3">
                <div className="text-sm font-bold text-gray-800">Overall Recommendation</div>
                <div className="space-y-2">
                    {["Recommend", "Recommend with reservations", "Do not recommend"].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                            <input type="radio" name={`rec-${candidate.id}`} checked={form.recommendation === opt} onChange={() => setForm(f => ({ ...f, recommendation: opt as any }))} />
                            {opt}
                        </label>
                    ))}
                </div>
                {(form.recommendation === "Recommend with reservations" || form.recommendation === "Do not recommend") && (
                    <textarea rows={3} placeholder="Please explain your reservations or concerns..." className="w-full border border-yellow-300 rounded px-3 py-2 text-sm focus:outline-none" />
                )}
            </div>

            <div className="p-4 border border-gray-200 rounded space-y-3">
                <div className="text-sm font-bold text-gray-800">Optional: Your Interaction with the Candidate</div>
                <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="radio" name={`conv-${candidate.id}`} checked={form.hadConversation === "Yes"} onChange={() => setForm(f => ({ ...f, hadConversation: "Yes" }))} /> Yes, we spoke</label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="radio" name={`conv-${candidate.id}`} checked={form.hadConversation === "No"} onChange={() => setForm(f => ({ ...f, hadConversation: "No" }))} /> I reviewed materials only</label>
                </div>
                {form.hadConversation === "Yes" && (
                    <textarea rows={2} placeholder="Briefly describe your interaction..." className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#003d45]" />
                )}
            </div>

            <button
                onClick={() => setSubmitted(true)}
                className="px-8 py-2.5 bg-[#003d45] text-white rounded text-sm font-semibold hover:bg-[#004f5c] transition-colors"
            >
                Submit Evaluation
            </button>
        </div>
    );
}

function ReferenceForm() {
    const [selected, setSelected] = useState(PENDING_REF_CANDIDATES[0]);

    return (
        <div className="flex flex-col md:flex-row gap-6 md:min-h-[600px]">
            {/* Left: Candidate list */}
            <div className="w-full md:w-48 shrink-0">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Pending Evaluations</div>
                <div className="space-y-1">
                    {PENDING_REF_CANDIDATES.map(c => (
                        <button
                            key={c.id}
                            onClick={() => setSelected(c)}
                            className={`w-full text-left px-3 py-2.5 rounded text-sm transition-all ${selected.id === c.id ? "bg-[#003d45] text-white" : "hover:bg-gray-100 text-gray-700"}`}
                        >
                            <div className="font-semibold truncate">{c.name}</div>
                            <div className={`text-xs mt-0.5 ${selected.id === c.id ? "text-white/60" : "text-gray-400"}`}>{c.employer}</div>
                            <div className={`text-xs ${selected.id === c.id ? "text-white/60" : "text-gray-400"}`}>Path {c.path} · {c.yearsExp} yrs</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Right: Evaluation form for selected candidate */}
            <div className="flex-1 min-w-0">
                {/* Candidate header */}
                <div className="p-4 border border-gray-200 rounded mb-5">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{selected.name}</h2>
                            <div className="text-sm text-gray-500">{selected.employer}</div>
                        </div>
                        <span className="px-3 py-1 bg-[#003d45]/10 text-[#003d45] rounded font-semibold text-sm">Path {selected.path}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                        <div><div className="text-gray-400">Experience</div><div className="font-semibold">{selected.yearsExp} yrs</div></div>
                        {selected.l3PassDate && <div><div className="text-gray-400">L3 Pass</div><div className="font-semibold">{selected.l3PassDate}</div></div>}
                        <div><div className="text-gray-400">Your role</div><div className="font-semibold">{selected.refName}</div></div>
                    </div>
                </div>

                <RefEvalForm key={selected.id} candidate={selected} />
            </div>
        </div>
    );
}

// ─── View 3: Committee Review ──────────────────────────────────────────────────

function CommitteeReview({ candidates }: { candidates: Candidate[] }) {
    const readyCandidates = candidates.filter(c => c.status === "Ready for Committee" || c.status === "Under Committee Review");
    const [selected, setSelected] = useState<Candidate | null>(readyCandidates[0] ?? null);
    const [activeTab, setActiveTab] = useState<"work" | "references" | "sample">("work");
    const [submitted, setSubmitted] = useState<Record<string, boolean>>({});
    const [checklist, setChecklist] = useState({
        s1Context: "" as "" | "Pass" | "Fail",
        s1Accountability: "" as "" | "Pass" | "Fail",
        s1Outputs: "" as "" | "Pass" | "Fail",
        s1Cadence: "" as "" | "Pass" | "Fail",
        s2WorkEx: "" as "" | "Yes" | "No" | "Needs More Info",
        s2Technical: "" as "" | "Yes" | "No" | "Needs More Info",
        s2Conduct: "" as "" | "Yes" | "No" | "Needs More Info",
        s4Sufficient: "" as "" | "Yes" | "No" | "Request more",
        s4Defer: "" as "" | "Yes" | "No",
        s4Flag: "" as "" | "Yes" | "No",
        s5Decision: "" as "" | "Recommend Approval" | "Defer Pending Additional Information" | "Flag for Board Consideration",
        s5Notes: "",
    });

    if (!selected) {
        return <div className="text-center py-16 text-gray-400">No candidates currently ready for committee review.</div>;
    }

    const hasAIConcern = selected.references.some(r => r.aiOriginality === "Unsure" || r.aiOriginality === "No");

    return (
        <div className="flex flex-col md:flex-row gap-6 md:min-h-[600px]">
            {/* Left: Candidate list */}
            <div className="w-full md:w-48 shrink-0">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Review Queue</div>
                <div className="space-y-1">
                    {readyCandidates.map(c => (
                        <button
                            key={c.id}
                            onClick={() => setSelected(c)}
                            className={`w-full text-left px-3 py-2 rounded text-sm transition-all ${selected.id === c.id ? "bg-[#003d45] text-white" : "hover:bg-gray-100 text-gray-700"}`}
                        >
                            <div className="font-semibold truncate">{c.name}</div>
                            <div className="text-xs opacity-60">Path {c.path}</div>
                            {submitted[c.id] && <div className="text-xs text-green-400 font-semibold">✓ Reviewed</div>}
                        </button>
                    ))}
                </div>
            </div>

            {/* Middle: Candidate file */}
            <div className="flex-1 min-w-0">
                {/* Header */}
                <div className="p-4 border border-gray-200 rounded mb-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{selected.name}</h2>
                            <div className="text-sm text-gray-500">{selected.employer} · {selected.location}</div>
                        </div>
                        <span className="px-3 py-1 bg-[#003d45]/10 text-[#003d45] rounded font-semibold text-sm">Path {selected.path}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
                        <div><div className="text-gray-400">Applied</div><div className="font-semibold">{selected.dateApplied}</div></div>
                        <div><div className="text-gray-400">Experience</div><div className="font-semibold">{selected.yearsExperience} yrs</div></div>
                        {selected.l3PassDate && <div><div className="text-gray-400">L3 Pass</div><div className="font-semibold">{selected.l3PassDate}</div></div>}
                        <div>
                            <div className="text-gray-400">References</div>
                            <div className="font-semibold text-green-600">{selected.references.filter(r => r.status === "complete").length}/3 in</div>
                        </div>
                    </div>
                    {hasAIConcern && (
                        <div className="mt-3 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-800 font-semibold">
                            ⚠ One or more references flagged a concern about the originality of this work sample.
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-3 border-b border-gray-200">
                    {(["work", "references", "sample"] as const).map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm font-semibold capitalize transition-all ${activeTab === tab ? "border-b-2 border-[#003d45] text-[#003d45]" : "text-gray-400 hover:text-gray-700"}`}>
                            {tab === "work" ? "Work Experience" : tab === "references" ? "References" : "Work Sample"}
                        </button>
                    ))}
                </div>

                {activeTab === "work" && (
                    <div className="space-y-3">
                        {[
                            { label: "CONTEXT", value: selected.workExperience.context },
                            { label: "ACCOUNTABILITY", value: selected.workExperience.accountability },
                            { label: "OUTPUTS", value: selected.workExperience.outputs },
                            { label: "CADENCE", value: selected.workExperience.cadence },
                        ].map(({ label, value }) => (
                            <div key={label} className="p-3 border border-gray-200 rounded">
                                <div className="text-[10px] font-bold tracking-widest text-[#003d45] mb-1">[{label}]</div>
                                <div className="text-sm text-gray-700">{value}</div>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === "references" && (
                    <div className="space-y-4">
                        {selected.references.map((ref, i) => (
                            <div key={i} className="p-4 border border-gray-200 rounded">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="font-semibold text-sm">{ref.name || `Reference ${i + 1}`}</div>
                                        <div className="text-xs text-gray-400">{ref.email}</div>
                                    </div>
                                    <RefStatusBadge status={ref.status} />
                                </div>
                                {ref.scores && (
                                    <>
                                        <div className="flex gap-3 mb-2">
                                            {["Professional Engagement", "Quality of Work", "Professional Character", "Community Fit"].map((dim, di) => (
                                                <div key={di} className="text-center">
                                                    <ScoreDot score={[ref.scores!.d1, ref.scores!.d2, ref.scores!.d3, ref.scores!.d4][di]} />
                                                    <div className="text-[9px] text-gray-400 mt-0.5 max-w-[48px] leading-tight">{dim}</div>
                                                </div>
                                            ))}
                                        </div>
                                        {ref.comments && (
                                            <div className="space-y-1 text-xs text-gray-600">
                                                {ref.comments.d1 && <p><strong>Engagement:</strong> {ref.comments.d1}</p>}
                                                {ref.comments.d4 && <p><strong>Community:</strong> {ref.comments.d4}</p>}
                                            </div>
                                        )}
                                        <div className="mt-2 flex items-center gap-3 text-xs">
                                            <span className={`font-semibold ${ref.recommendation === "Recommend" ? "text-green-600" : "text-yellow-600"}`}>{ref.recommendation}</span>
                                            <span className="text-gray-400">AI Originality: <span className={ref.aiOriginality === "Yes" ? "text-green-600" : "text-yellow-600"}>{ref.aiOriginality}</span></span>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === "sample" && (
                    <div className="p-6 border-2 border-dashed border-gray-300 rounded text-center">
                        <div className="text-3xl mb-3">📄</div>
                        <p className="text-sm font-semibold text-gray-600">{selected.name} — Work Sample</p>
                        <p className="text-xs text-gray-400 mt-1">Uploaded 2026-03-20 · PDF · 2.4 MB</p>
                        <button className="mt-3 px-4 py-1.5 bg-gray-100 border border-gray-300 rounded text-sm hover:bg-gray-200">Download</button>
                    </div>
                )}
            </div>

            {/* Right: Checklist */}
            <div className="w-64 shrink-0">
                <div className="border border-gray-200 rounded overflow-hidden">
                    <div className="bg-[#003d45] text-white px-3 py-2 text-xs font-bold">Committee Evaluation — {selected.name.split(" ")[0]}</div>
                    <div className="p-3 space-y-4 text-xs overflow-y-auto max-h-[550px]">

                        {/* Section 1 */}
                        <div>
                            <div className="font-bold text-gray-700 mb-2">1. Work Experience</div>
                            {[
                                { label: "Context", key: "s1Context" },
                                { label: "Accountability", key: "s1Accountability" },
                                { label: "Outputs", key: "s1Outputs" },
                                { label: "Cadence", key: "s1Cadence" },
                            ].map(({ label, key }) => (
                                <div key={key} className="flex items-center justify-between py-1 border-b border-gray-100">
                                    <span className="text-gray-600">{label}</span>
                                    <div className="flex gap-1">
                                        {["Pass", "Fail"].map(opt => (
                                            <button key={opt} onClick={() => setChecklist(c => ({ ...c, [key]: opt }))} className={`px-2 py-0.5 rounded text-[10px] font-bold border transition-all ${(checklist as any)[key] === opt ? (opt === "Pass" ? "bg-green-500 text-white border-green-500" : "bg-red-500 text-white border-red-500") : "border-gray-300 text-gray-400"}`}>{opt}</button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Section 2 */}
                        <div>
                            <div className="font-bold text-gray-700 mb-2">2. Core Criteria</div>
                            {[
                                { label: "Approved Work Ex.", key: "s2WorkEx" },
                                { label: "Technical Competence", key: "s2Technical" },
                                { label: "Ethical Conduct", key: "s2Conduct" },
                            ].map(({ label, key }) => (
                                <div key={key} className="py-1 border-b border-gray-100">
                                    <div className="text-gray-600 mb-1">{label}</div>
                                    <div className="flex gap-1 flex-wrap">
                                        {["Yes", "No", "Needs More Info"].map(opt => (
                                            <button key={opt} onClick={() => setChecklist(c => ({ ...c, [key]: opt }))} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-all ${(checklist as any)[key] === opt ? "bg-[#003d45] text-white border-[#003d45]" : "border-gray-300 text-gray-400"}`}>{opt}</button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Section 3 */}
                        <div>
                            <div className="font-bold text-gray-700 mb-2">3. Bylaws Compliance</div>
                            <div className="flex items-center justify-between py-1">
                                <span className="text-gray-600">Path verified</span>
                                <span className="text-green-600 font-semibold">✓ Auto-verified</span>
                            </div>
                            <div className="flex items-center justify-between py-1">
                                <span className="text-gray-600">Sponsorship met</span>
                                <span className="text-green-600 font-semibold">✓ Auto-verified</span>
                            </div>
                        </div>

                        {/* Section 4 */}
                        <div>
                            <div className="font-bold text-gray-700 mb-2">4. Consideration</div>
                            <div className="py-1 border-b border-gray-100">
                                <div className="text-gray-600 mb-1">Documentation sufficient?</div>
                                <div className="flex gap-1">
                                    {["Yes", "No", "Request more"].map(opt => (
                                        <button key={opt} onClick={() => setChecklist(c => ({ ...c, s4Sufficient: opt as any }))} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-all ${checklist.s4Sufficient === opt ? "bg-[#003d45] text-white border-[#003d45]" : "border-gray-300 text-gray-400"}`}>{opt}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="py-1 border-b border-gray-100">
                                <div className="text-gray-600 mb-1">Defer for more info?</div>
                                <div className="flex gap-1">
                                    {["Yes", "No"].map(opt => (
                                        <button key={opt} onClick={() => setChecklist(c => ({ ...c, s4Defer: opt as any }))} className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-all ${checklist.s4Defer === opt ? "bg-[#003d45] text-white border-[#003d45]" : "border-gray-300 text-gray-400"}`}>{opt}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="py-1">
                                <div className="text-gray-600 mb-1">Flag for Board?</div>
                                <div className="flex gap-1">
                                    {["Yes", "No"].map(opt => (
                                        <button key={opt} onClick={() => setChecklist(c => ({ ...c, s4Flag: opt as any }))} className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-all ${checklist.s4Flag === opt ? "bg-[#003d45] text-white border-[#003d45]" : "border-gray-300 text-gray-400"}`}>{opt}</button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Section 5 */}
                        <div>
                            <div className="font-bold text-gray-700 mb-2">5. Official Recommendation</div>
                            {["Recommend Approval", "Defer Pending Additional Information", "Flag for Board Consideration"].map(opt => (
                                <label key={opt} className="flex items-start gap-1.5 py-1 cursor-pointer">
                                    <input type="radio" name={`s5-${selected.id}`} checked={checklist.s5Decision === opt} onChange={() => setChecklist(c => ({ ...c, s5Decision: opt as any }))} className="mt-0.5" />
                                    <span className="text-gray-600">{opt}</span>
                                </label>
                            ))}
                            <textarea rows={2} placeholder="Notes..." className="w-full mt-2 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none" value={checklist.s5Notes} onChange={e => setChecklist(c => ({ ...c, s5Notes: e.target.value }))} />
                        </div>

                        <button
                            onClick={() => setSubmitted(s => ({ ...s, [selected.id]: true }))}
                            className="w-full py-2 bg-[#003d45] text-white rounded text-xs font-bold hover:bg-[#004f5c] transition-colors"
                        >
                            {submitted[selected.id] ? "✓ Submitted" : "Submit Evaluation"}
                        </button>
                        {submitted[selected.id] && <p className="text-center text-green-600 font-semibold text-xs">Your evaluation has been recorded.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── View 4: Barbara's Dashboard ──────────────────────────────────────────────

function BarbaraDashboard({ candidates }: { candidates: Candidate[] }) {
    const [view, setView] = useState<"kanban" | "list">("kanban");

    const kanbanCols: { status: CandidateStatus; label: string }[] = [
        { status: "Invited", label: "Invited" },
        { status: "Preparing", label: "Preparing" },
        { status: "References in Progress", label: "References" },
        { status: "Ready for Committee", label: "Ready" },
        { status: "Under Committee Review", label: "In Review" },
        { status: "Committee Approved", label: "Approved" },
        { status: "Board Approved — Processing", label: "Processing" },
        { status: "Certificate in Production", label: "Cert. Prod." },
        { status: "Certificate Shipped", label: "Shipped" },
        { status: "Chartered", label: "Chartered ✓" },
    ];

    const extraCols: { status: CandidateStatus; label: string }[] = [
        { status: "Parked — Building Experience", label: "Parked" },
        { status: "Flagged for Board", label: "Flagged" },
    ];

    const attention = [
        { label: "Tanmay Sharma", note: "References stuck (1/3 for 18 days)", urgency: "red" as const },
        { label: "Logan Mitchell", note: "Payment outstanding (5 days since approval)", urgency: "yellow" as const },
        { label: "Welcome call needed", note: "Nicola Ferretti, Andrew Park", urgency: "yellow" as const },
        { label: "Shubham Desai", note: "AI originality concern flagged by 2 references", urgency: "red" as const },
        { label: "Nadezda Kovacs", note: "Parked candidate check-in due (45 days)", urgency: "yellow" as const },
    ];

    const stats = [
        { label: "Applications this month", value: "7" },
        { label: "Approved this month", value: "3" },
        { label: "Avg processing time", value: "34 days" },
        { label: "L3 → Application rate", value: "38%" },
    ];

    const CASCADE_STEPS = ["Payment", "Status Change", "Notification", "Certificate Order", "Welcome Call", "Newsletter"];

    return (
        <div className="space-y-4">
            {/* Stats bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {stats.map(({ label, value }) => (
                    <div key={label} className="p-3 border border-gray-200 rounded bg-white">
                        <div className="text-xl font-black text-[#003d45]">{value}</div>
                        <div className="text-xs text-gray-400">{label}</div>
                    </div>
                ))}
            </div>

            {/* Attention Required */}
            <div className="p-3 border border-amber-200 bg-amber-50 rounded">
                <div className="text-xs font-bold text-amber-800 mb-2 uppercase tracking-wide">Attention Required</div>
                <div className="space-y-1">
                    {attention.map(({ label, note, urgency }) => (
                        <div key={label} className="flex items-center gap-2 text-sm">
                            <UrgencyDot urgency={urgency} />
                            <strong>{label}</strong>
                            <span className="text-gray-500">—</span>
                            <span className="text-gray-600">{note}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">View:</span>
                <button onClick={() => setView("kanban")} className={`px-3 py-1 rounded text-xs font-semibold ${view === "kanban" ? "bg-[#003d45] text-white" : "border border-gray-300 text-gray-600"}`}>Kanban</button>
                <button onClick={() => setView("list")} className={`px-3 py-1 rounded text-xs font-semibold ${view === "list" ? "bg-[#003d45] text-white" : "border border-gray-300 text-gray-600"}`}>List</button>
            </div>

            {view === "kanban" && (
                <div className="overflow-x-auto">
                    <div className="flex gap-3 min-w-max pb-3">
                        {[...kanbanCols, ...extraCols].map(({ status, label }) => {
                            const cols = candidates.filter(c => c.status === status);
                            return (
                                <div key={status} className="w-44 shrink-0">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</span>
                                        {cols.length > 0 && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded-full text-[10px] font-bold">{cols.length}</span>}
                                    </div>
                                    <div className="space-y-2 min-h-[60px]">
                                        {cols.map(c => (
                                            <div key={c.id} className="p-2.5 bg-white border border-gray-200 rounded shadow-sm">
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <UrgencyDot urgency={c.urgency} />
                                                    <span className="text-xs font-semibold text-gray-800 truncate">{c.name}</span>
                                                </div>
                                                <div className="text-[10px] text-gray-400">{c.daysInState}d in state</div>
                                                {c.status === "References in Progress" && (
                                                    <div className="mt-1 text-[10px] font-semibold text-amber-600">
                                                        {c.references.filter(r => r.status === "complete").length}/3 refs
                                                    </div>
                                                )}
                                                {c.status === "Board Approved — Processing" && c.cascadeStep && (
                                                    <div className="mt-1 text-[10px] text-blue-600 font-semibold">
                                                        {CASCADE_STEPS.slice(0, c.cascadeStep).join(" → ")}…
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {cols.length === 0 && <div className="h-10 border-2 border-dashed border-gray-100 rounded" />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {view === "list" && (
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">Candidate</th>
                            <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">Status</th>
                            <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">Days</th>
                            <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">Urgency</th>
                        </tr>
                    </thead>
                    <tbody>
                        {candidates.map(c => (
                            <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-2 px-3">
                                    <div className="font-semibold">{c.name}</div>
                                    <div className="text-xs text-gray-400">{c.employer}</div>
                                </td>
                                <td className="py-2 px-3"><StatusBadge status={c.status} /></td>
                                <td className="py-2 px-3 text-gray-600">{c.daysInState}d</td>
                                <td className="py-2 px-3"><UrgencyDot urgency={c.urgency} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ─── View 5: Board Queue ───────────────────────────────────────────────────────

function BoardQueue({ candidates, onApprove }: { candidates: Candidate[]; onApprove: (ids: string[]) => void }) {
    const boardCandidates = candidates.filter(c => c.status === "Committee Approved" || c.status === "Flagged for Board");
    const [selected, setSelected] = useState<Set<string>>(new Set(boardCandidates.map(c => c.id)));
    const [confirmed, setConfirmed] = useState(false);
    const [approvalDone, setApprovalDone] = useState(false);

    const toggleAll = () => {
        if (selected.size === boardCandidates.length) setSelected(new Set());
        else setSelected(new Set(boardCandidates.map(c => c.id)));
    };

    const handleApprove = () => {
        onApprove(Array.from(selected));
        setApprovalDone(true);
        setConfirmed(false);
    };

    if (approvalDone) {
        return (
            <div className="max-w-xl mx-auto mt-16 text-center p-8 border border-green-200 bg-green-50 rounded-lg">
                <div className="text-4xl mb-4">✓</div>
                <h2 className="text-xl font-bold text-green-800 mb-2">{selected.size} Candidate{selected.size !== 1 ? "s" : ""} Approved</h2>
                <p className="text-green-700">Candidates have been approved for Professional Membership. The post-approval cascade has been initiated. Barbara's dashboard will reflect the updated statuses.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-bold">Monthly Admissions Ratification — April 2026</h2>
                    <p className="text-sm text-gray-500">{boardCandidates.length} candidates recommended for approval · {boardCandidates.filter(c => c.path === "B").length} Path B · {boardCandidates.filter(c => c.path === "A").length} Path A</p>
                </div>
                <button onClick={toggleAll} className="px-4 py-1.5 border border-gray-300 rounded text-sm font-semibold hover:bg-gray-50">
                    {selected.size === boardCandidates.length ? "Deselect All" : "Select All"}
                </button>
            </div>

            <table className="w-full text-sm border-collapse border border-gray-200 rounded overflow-hidden">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="py-2 px-3 text-left w-8"></th>
                        <th className="py-2 px-3 text-left text-xs font-bold text-gray-500">Name</th>
                        <th className="py-2 px-3 text-left text-xs font-bold text-gray-500">Location</th>
                        <th className="py-2 px-3 text-left text-xs font-bold text-gray-500">Path</th>
                        <th className="py-2 px-3 text-left text-xs font-bold text-gray-500">Experience</th>
                        <th className="py-2 px-3 text-left text-xs font-bold text-gray-500">Committee</th>
                        <th className="py-2 px-3 text-left text-xs font-bold text-gray-500">References</th>
                        <th className="py-2 px-3 text-left text-xs font-bold text-gray-500"></th>
                    </tr>
                </thead>
                <tbody>
                    {boardCandidates.map(c => {
                        const avgScore = c.references.length > 0 && c.references[0].scores
                            ? (c.references.reduce((acc, r) => acc + (r.scores ? (r.scores.d1 + r.scores.d2 + r.scores.d3 + r.scores.d4) / 4 : 0), 0) / c.references.length).toFixed(1)
                            : "—";
                        return (
                            <tr key={c.id} className={`border-t border-gray-200 ${selected.has(c.id) ? "bg-blue-50" : "hover:bg-gray-50"}`}>
                                <td className="py-2 px-3">
                                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => {
                                        const next = new Set(selected);
                                        if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                                        setSelected(next);
                                    }} />
                                </td>
                                <td className="py-2 px-3 font-semibold">{c.name}</td>
                                <td className="py-2 px-3 text-gray-500 text-xs">{c.location}</td>
                                <td className="py-2 px-3"><span className="px-2 py-0.5 bg-[#003d45]/10 text-[#003d45] rounded text-xs font-bold">Path {c.path}</span></td>
                                <td className="py-2 px-3 text-gray-600">{c.yearsExperience} yrs</td>
                                <td className="py-2 px-3">
                                    <span className={`text-xs font-semibold ${c.status === "Flagged for Board" ? "text-rose-600" : "text-green-600"}`}>
                                        {c.status === "Flagged for Board" ? "Flagged" : "Approved"}
                                    </span>
                                </td>
                                <td className="py-2 px-3 text-xs text-gray-500">Avg {avgScore}/5</td>
                                <td className="py-2 px-3">
                                    <button className="text-xs text-gray-400 hover:text-red-600 transition-colors">Flag</button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {!confirmed ? (
                <button
                    disabled={selected.size === 0}
                    onClick={() => setConfirmed(true)}
                    className="px-8 py-2.5 bg-[#003d45] text-white rounded font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#004f5c] transition-colors"
                >
                    Approve Selected ({selected.size})
                </button>
            ) : (
                <div className="p-4 border border-amber-300 bg-amber-50 rounded">
                    <p className="font-semibold text-amber-900 mb-3">You are approving <strong>{selected.size} candidates</strong> for Professional Membership. This will initiate the post-approval cascade including payment processing, status changes, and certificate orders. Proceed?</p>
                    <div className="flex gap-3">
                        <button onClick={handleApprove} className="px-6 py-2 bg-[#003d45] text-white rounded font-semibold text-sm">Confirm Approval</button>
                        <button onClick={() => setConfirmed(false)} className="px-4 py-2 border border-gray-300 rounded text-sm">Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

const VIEWS = [
    { id: "form", label: "Candidate Form" },
    { id: "reference", label: "Reference Evaluation" },
    { id: "committee", label: "Committee Review" },
    { id: "dashboard", label: "Staff Dashboard" },
    { id: "board", label: "Board Queue" },
] as const;

type ViewId = typeof VIEWS[number]["id"];

export default function AdmissionsPrototype({ embedded = false }: { embedded?: boolean }) {
    const [activeView, setActiveView] = useState<ViewId>("dashboard");
    const [candidates, setCandidates] = useState<Candidate[]>(initialCandidates);

    const handleNewCandidate = (c: Candidate) => {
        setCandidates(prev => [...prev, c]);
    };

    const handleApprove = (ids: string[]) => {
        setCandidates(prev => prev.map(c =>
            ids.includes(c.id)
                ? { ...c, status: "Board Approved — Processing" as CandidateStatus, cascadeStep: 1, daysInState: 0 }
                : c
        ));
    };

    const subtitles: Record<ViewId, string> = {
        form: "Fill out the application to join as a Professional Member.",
        reference: "Evaluating: Sarah Chen (sample reference form)",
        committee: "Reviewing as: Louis Spector (Admissions Committee)",
        dashboard: "All active applications at a glance — sorted by urgency.",
        board: "Reviewing as: Tyler Wood (CEO) · April 2026 Ratification",
    };

    if (embedded) {
        return (
            <div className="space-y-4">
                {/* Tab bar */}
                <div className="flex gap-1 border-b border-gray-200 pb-0">
                    {VIEWS.map(({ id, label }) => (
                        <button
                            key={id}
                            onClick={() => setActiveView(id)}
                            className={`px-4 py-2.5 text-sm font-semibold rounded-t transition-all border-b-2 ${
                                activeView === id
                                    ? "border-[#003d45] text-[#003d45] bg-white"
                                    : "border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Subtitle */}
                <p className="text-sm text-gray-500">{subtitles[activeView]}</p>

                {/* Panel content */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    {activeView === "form" && <CandidateForm onSubmit={handleNewCandidate} />}
                    {activeView === "reference" && <ReferenceForm />}
                    {activeView === "committee" && <CommitteeReview candidates={candidates} />}
                    {activeView === "dashboard" && <BarbaraDashboard candidates={candidates} />}
                    {activeView === "board" && <BoardQueue candidates={candidates} onApprove={handleApprove} />}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Prototype header */}
            <div className="bg-[#003d45] text-white px-6 py-2 flex items-center gap-3">
                <span className="text-xs font-bold uppercase tracking-widest opacity-60">Prototype v1.0</span>
                <span className="text-white/30">|</span>
                <span className="text-xs text-white/60">Admissions Workflow — Demo Mode · Data not saved automatically</span>
            </div>

            {/* Sidebar + Content */}
            <div className="flex flex-col md:flex-row md:min-h-[calc(100vh-80px)]">
                {/* Sidebar nav */}
                <aside className="w-full md:w-52 bg-white border-b md:border-b-0 md:border-r border-gray-200 p-4 shrink-0">
                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Admissions Views</div>
                    <nav className="flex md:block gap-1 space-y-0 md:space-y-1 overflow-x-auto [&::-webkit-scrollbar]:hidden">
                        {VIEWS.map(({ id, label }) => (
                            <button
                                key={id}
                                onClick={() => setActiveView(id)}
                                className={`w-auto md:w-full whitespace-nowrap text-left px-3 py-2 rounded text-sm font-semibold transition-all ${activeView === id ? "bg-[#003d45] text-white" : "text-gray-600 hover:bg-gray-100"}`}
                            >
                                {label}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Main content */}
                <main className="flex-1 p-6 overflow-auto">
                    <div className="mb-6">
                        <h1 className="text-2xl font-black text-gray-900">
                            {VIEWS.find(v => v.id === activeView)?.label}
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">{subtitles[activeView]}</p>
                    </div>

                    {activeView === "form" && <CandidateForm onSubmit={handleNewCandidate} />}
                    {activeView === "reference" && <ReferenceForm />}
                    {activeView === "committee" && <CommitteeReview candidates={candidates} />}
                    {activeView === "dashboard" && <BarbaraDashboard candidates={candidates} />}
                    {activeView === "board" && <BoardQueue candidates={candidates} onApprove={handleApprove} />}
                </main>
            </div>
        </div>
    );
}
